import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CsrAPI } from "../apis/CsrAPI";
import { FILE_UPLOAD_STATUS, PN_DOCUMENT_TYPE } from "../utils/Constants";

const POLL_INTERVAL_MS = 10000;

/**
 * Owns Patient Narrative's own source-document list, upload, and delete -
 * hits /patient-narratives/{id}/... only, never /studies/... . Deliberately
 * not dependent on CsrProvider/useCsr: PN documents live under
 * PatientNarrativeModel via the shared DocumentModel, not StudyModel, and a
 * linked_csr_id has no effect on this list (see
 * `patient_narratives_routes_handlers.list_documents`).
 *
 * Without pn:files:read (`canRead`) the list is never fetched and nothing
 * polls: the page shows no documents rather than an empty list the backend
 * would have refused to serve anyway.
 *
 * Polls while any document is still UPLOADED (not yet PROCESSED/ERROR) -
 * same convention as CsrProvider, but scoped to this PN's own documents
 * only, and with no connection to csrDataPollingSingleton/table-figure
 * polling/traffic lights.
 */
export default function usePatientNarrativeDocuments(
  patientNarrativeId,
  { canRead = true } = {}
) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wrongEnvironment, setWrongEnvironment] = useState(false);

  const mountedRef = useRef(true);
  const pollRef = useRef(null);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    if (!patientNarrativeId || !canRead) return Promise.resolve();
    return CsrAPI.getPatientNarrativeDocuments({ patientNarrativeId })
      .then(docs => {
        if (!mountedRef.current) return;
        setDocuments(docs);
        setError(null);
      })
      .catch(err => {
        if (!mountedRef.current) return;
        if (err.response?.status === 403) {
          setWrongEnvironment(true);
        } else {
          setError(err);
        }
      });
  }, [patientNarrativeId, canRead]);

  useEffect(() => {
    if (!patientNarrativeId) return undefined;
    if (!canRead) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    refresh().finally(() => {
      if (!cancelled && mountedRef.current) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [patientNarrativeId, refresh, canRead]);

  useEffect(() => {
    const hasPending = documents.some(
      doc => doc.status === FILE_UPLOAD_STATUS.uploaded
    );

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (hasPending) {
      pollRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [documents, refresh]);

  const upload = useCallback(
    (file, docType, onUploadProgress) =>
      CsrAPI.uploadPatientNarrativeDocument(
        { patientNarrativeId, file, docType },
        onUploadProgress
      ).then(result => {
        if (mountedRef.current) refresh();
        return result;
      }),
    [patientNarrativeId, refresh]
  );

  const remove = useCallback(
    documentId =>
      CsrAPI.deletePatientNarrativeDocument({
        patientNarrativeId,
        documentId,
      }).then(result => {
        if (mountedRef.current) refresh();
        return result;
      }),
    [patientNarrativeId, refresh]
  );

  // Memoized on `documents` alone - a stable reference across renders that
  // didn't change `documents` is required, since consumers (e.g.
  // pn/FileUpload.js) key their own effects off documentsByType. Without
  // this, a brand-new object/arrays on every render make any such effect
  // see "changed" deps every render, re-run every render, and (if it calls
  // setState) loop forever.
  const documentsByType = useMemo(
    () => ({
      [PN_DOCUMENT_TYPE.listing]: documents.filter(
        doc => doc.document_type === PN_DOCUMENT_TYPE.listing
      ),
      [PN_DOCUMENT_TYPE.cioms]: documents.filter(
        doc => doc.document_type === PN_DOCUMENT_TYPE.cioms
      ),
    }),
    [documents]
  );

  return {
    documents,
    documentsByType,
    loading,
    error,
    wrongEnvironment,
    refresh,
    upload,
    remove,
  };
}
